import { BulkOperation_Read, BulkOperationStatus } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { noop } from 'lodash-es';
import { createContext, useMemo } from 'react';

import useBulkOperationProgress from '~hooks/useBulkOperationProgress/useBulkOperationProgress';
import { realTimeChannels } from '~services/realTimeChannels';

type OutboundEmailProgressContextType = {
  enqueueOutboundEmail: (operationId: string, recipientCount: number) => void;
};

const OutboundEmailProgressContext = createContext<OutboundEmailProgressContextType>({
  enqueueOutboundEmail: noop,
});

function outboundEmailStatusText(progress: BulkOperation_Read) {
  if (progress.status === BulkOperationStatus.ERROR) {
    return t('common.outbound_email_progress.stopped');
  }

  if (progress.status !== BulkOperationStatus.FINISHED) {
    return t('common.outbound_email_progress.sending', {
      count: progress.count,
      processed: progress.succeeded_count + progress.failed_count,
    });
  }

  if (progress.failed_count > 0) {
    return t('common.outbound_email_progress.failed', { count: progress.failed_count });
  }

  return t('common.outbound_email_progress.sent', { count: progress.succeeded_count });
}

function OutboundEmailProgressContextProvider({ children }: React.PropsWithChildren) {
  const { progressStack, track } = useBulkOperationProgress<BulkOperation_Read>({
    channel: realTimeChannels.OutboundEmailChannel,
    statusText: outboundEmailStatusText,
    title: t('common.outbound_email_progress.title'),
  });

  const contextValue = useMemo(() => ({ enqueueOutboundEmail: track }), [track]);

  return (
    <OutboundEmailProgressContext.Provider value={contextValue}>
      {children}
      {progressStack}
    </OutboundEmailProgressContext.Provider>
  );
}

export { OutboundEmailProgressContextProvider };
export default OutboundEmailProgressContext;
